import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import {
  ObfuscationService,
  ObfuscationType,
} from '@principle-theorem/ng-management-tools';
import { NgCommonModule, TrackByFunctions } from '@principle-theorem/ng-shared';
import { CustomReport } from '@principle-theorem/principle-core';
import {
  type ICustomReportChart,
  type ICustomReportChartSection,
} from '@principle-theorem/principle-core/interfaces';
import { ReportBuilderDataSource } from '@principle-theorem/reporting';
import { IReportBuilderDataSource } from '@principle-theorem/reporting/interfaces';
import { flatten } from 'lodash';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportBuilderStore } from '../../report-builder.store';
import { chartsToDisplays } from '../report-builder-chart-section/report-builder-chart-section.component';
import { type IChartDisplay } from '../report-builder-chart-section/report-builder-chart/report-builder-chart-display';
import { ReportBuilderChartComponent } from '../report-builder-chart-section/report-builder-chart/report-builder-chart.component';

@Component({
  selector: 'pr-report-builder-available-charts',
  templateUrl: './report-builder-available-charts.component.html',
  styleUrls: ['./report-builder-available-charts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    MatIconButton,
    MatTooltip,
    NgCommonModule,
    ReportBuilderChartComponent,
  ],
})
export class ReportBuilderAvailableChartsComponent {
  private _obfuscation = inject(ObfuscationService);
  trackBySection = TrackByFunctions.uniqueId<ICustomReportChartSection>();
  trackByChart = TrackByFunctions.uniqueId<ICustomReportChart>();
  trackByDisplay = TrackByFunctions.nestedField<IChartDisplay>('chart.uid');
  sections$: Observable<ICustomReportChartSection[]>;

  constructor(
    public store: ReportBuilderStore,
    private _snackBar: MatSnackBar
  ) {
    this.sections$ = this.store.dataSource$.pipe(
      map((dataSource) => this._buildChartSections(dataSource))
    );
  }

  getDisplays$(
    section: ICustomReportChartSection
  ): Observable<IChartDisplay[]> {
    const labelTransformFn = (label: string, piiType?: string): string =>
      this._obfuscation.transform(
        label,
        (piiType as ObfuscationType) ?? 'name'
      );
    return combineLatest([
      this.store.dataSource$,
      this.store.ndx$,
      this._obfuscation.isActive$,
    ]).pipe(
      map(([dataSource, ndx, _isActive]) =>
        chartsToDisplays(section.charts, dataSource, ndx, labelTransformFn)
      )
    );
  }

  async includeInCustomReport(display: IChartDisplay): Promise<void> {
    await this.store.addChart(display.chart);
    this._snackBar.open('Chart added to custom report');
  }

  isInCustomReport$(chartDisplay: IChartDisplay): Observable<boolean> {
    return this.store.display$.pipe(
      map((display) =>
        flatten(display?.chartSections.map((section) => section.charts)).some(
          (chart) => chart.uid === chartDisplay.chart.uid
        )
      )
    );
  }

  private _buildChartSections(
    dataSource?: IReportBuilderDataSource
  ): ICustomReportChartSection[] {
    if (!dataSource) {
      return [];
    }
    const availableCharts = dataSource.availableCharts.filter(
      (chart) => chart.availableInEditMode
    );

    return ReportBuilderDataSource.groupBySection(
      availableCharts,
      (chart) => chart.id
    ).map(({ section, items }) => {
      const charts = items.map((chart) => chart.chart);
      return CustomReport.chartSection(section, charts);
    });
  }
}
